import { useEffect, useRef, useState } from 'react'

let previewCount = 0

/**
 * Carrega a fonte do modal direto no navegador (`FontFace`), para a prévia mostrar o ajuste de
 * tamanho enquanto o controle se move — antes de a fonte existir na API, no caso do envio.
 *
 * Cada ajuste é uma família nova, porque o `size-adjust` é fixo por `FontFace`. A anterior só sai
 * do `document.fonts` quando a nova já carregou: removida antes, o texto piscaria na fonte
 * reserva a cada passo do controle.
 *
 * `isInvalid` é o arquivo que o navegador não conseguiu ler — um `.ttf` que não é fonte, por
 * exemplo. É a mesma leitura que o site faria, então vale como aviso antes do envio.
 */
export function useFontPreview(source: File | string | null, sizeAdjust: number) {
  const [family, setFamily] = useState<string>()
  const [isInvalid, setIsInvalid] = useState(false)
  const currentFace = useRef<FontFace>()
  const buffer = useRef<{ file: File; data: Promise<ArrayBuffer> }>()

  useEffect(() => {
    if (!source) {
      setFamily(undefined)
      setIsInvalid(false)
      return
    }

    // O aviso de arquivo inválido era do arquivo anterior: não vale para este enquanto ele carrega.
    setIsInvalid(false)
    let cancelled = false

    const load = async () => {
      let data: string | ArrayBuffer

      if (typeof source === 'string') {
        data = `url(${JSON.stringify(source)})`
      } else {
        if (buffer.current?.file !== source) buffer.current = { file: source, data: source.arrayBuffer() }
        data = await buffer.current.data
      }

      previewCount += 1
      const nextFamily = `abra-font-preview-${previewCount}`
      // `sizeAdjust` ainda não está no `lib.dom` do TypeScript deste projeto.
      const face = new FontFace(nextFamily, data, { sizeAdjust: `${sizeAdjust}%` } as FontFaceDescriptors)
      await face.load()

      if (cancelled) return

      document.fonts.add(face)
      if (currentFace.current) document.fonts.delete(currentFace.current)
      currentFace.current = face
      setFamily(nextFamily)
      setIsInvalid(false)
    }

    load().catch(() => {
      if (!cancelled) setIsInvalid(true)
    })

    return () => {
      cancelled = true
    }
  }, [source, sizeAdjust])

  useEffect(
    () => () => {
      if (currentFace.current) document.fonts.delete(currentFace.current)
    },
    []
  )

  return { family: source ? family : undefined, isInvalid }
}
